import { ChangeEvent, FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { api, ApiError, getToken, setToken } from "./api";
import type {
  Appearance,
  Application,
  Candidate,
  CandidateInput,
  Dashboard,
  Job,
  SearchRun,
  User,
  View,
} from "./types";

type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DEFAULT_APPEARANCE: Appearance = {
  theme: "system-light",
  font_scale: 100,
  density: "auto",
};

const NAV_ITEMS: Array<{ id: View; label: string; icon: IconName }> = [
  { id: "today", label: "Сегодня", icon: "home" },
  { id: "jobs", label: "Вакансии", icon: "briefcase" },
  { id: "profiles", label: "Профили", icon: "users" },
  { id: "applications", label: "Отклики", icon: "send" },
  { id: "settings", label: "Настройки", icon: "settings" },
];

type IconName =
  | "home"
  | "briefcase"
  | "users"
  | "send"
  | "settings"
  | "refresh"
  | "download"
  | "external"
  | "check"
  | "clock"
  | "close"
  | "signal"
  | "shield"
  | "menu"
  | "eye"
  | "eyeOff"
  | "arrowLeft";

const ICON_PATHS: Record<IconName, ReactNode> = {
  home: <><path d="m3 11 9-8 9 8"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/></>,
  briefcase: <><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V4h8v3M3 12h18M10 12v2h4v-2"/></>,
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>,
  send: <><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></>,
  settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21h-4v-.09A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3v-4h.09A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3h4v.09A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.38.25.7.6.89 1H21v4h-.09c-.44.08-.84.3-1.1.6-.2.12-.34.26-.41.4Z"/></>,
  refresh: <><path d="M20 6v5h-5"/><path d="M4 18v-5h5"/><path d="M18.5 9A7 7 0 0 0 6.2 6.2L4 8M20 16l-2.2 1.8A7 7 0 0 1 5.5 15"/></>,
  download: <><path d="M12 3v12"/><path d="m7 10 5 5 5-5"/><path d="M5 21h14"/></>,
  external: <><path d="M15 3h6v6"/><path d="m10 14 11-11"/><path d="M18 13v7H4V6h7"/></>,
  check: <path d="m5 12 4 4L19 6"/>,
  clock: <><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  close: <><path d="m6 6 12 12M18 6 6 18"/></>,
  signal: <><path d="M5 20v-4M10 20v-8M15 20V8M20 20V4"/></>,
  shield: <><path d="M12 3 4 6v5c0 5 3.4 8.4 8 10 4.6-1.6 8-5 8-10V6Z"/><path d="m9 12 2 2 4-5"/></>,
  menu: <><path d="M4 7h16M4 12h16M4 17h16"/></>,
  eye: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.5"/></>,
  eyeOff: <><path d="m3 3 18 18"/><path d="M10.6 6.2A10 10 0 0 1 12 6c6 0 9.5 6 9.5 6a15 15 0 0 1-2.1 2.8M6.2 6.2C3.8 7.9 2.5 12 2.5 12s3.5 6 9.5 6a9.8 9.8 0 0 0 3.5-.6"/><path d="M10.2 10.2a2.5 2.5 0 0 0 3.6 3.6"/></>,
  arrowLeft: <><path d="m15 18-6-6 6-6"/><path d="M9 12h11"/></>,
};

function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  return (
    <svg
      aria-hidden="true"
      className="icon"
      fill="none"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

function Logo() {
  return (
    <div className="brand">
      <div className="brand-mark" aria-hidden="true">CM</div>
      <div>
        <strong>CareerMove</strong>
        <span>точный поиск работы</span>
      </div>
    </div>
  );
}

function Button({
  children,
  className = "",
  icon,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { icon?: IconName }) {
  return (
    <button className={`button ${className}`.trim()} {...props}>
      {icon && <Icon name={icon} size={18} />}
      <span>{children}</span>
    </button>
  );
}

function EmptyState({
  title,
  text,
  action,
}: {
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon"><Icon name="signal" size={24} /></div>
      <h3>{title}</h3>
      <p>{text}</p>
      {action}
    </div>
  );
}

type AuthMode = "login" | "register" | "forgot" | "reset";

function AuthScreen({
  onAuthenticated,
  onInstall,
}: {
  onAuthenticated: (user: User) => void;
  onInstall: () => void;
}) {
  const [resetToken] = useState(
    () => new URLSearchParams(window.location.search).get("reset")?.trim() || "",
  );
  const [mode, setMode] = useState<AuthMode>(() => resetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [totp, setTotp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!resetToken) return;
    window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.hash}`);
  }, [resetToken]);

  function changeMode(next: AuthMode) {
    setMode(next);
    setChallenge("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setError("");
    setSuccess("");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      if (challenge) {
        const result = await api.totp(challenge, totp);
        setToken(result.token);
        onAuthenticated(result.user);
      } else if (mode === "forgot") {
        const result = await api.forgotPassword(email);
        setSuccess(result.message);
      } else if (mode === "reset") {
        if (password !== confirmPassword) throw new Error("Пароли не совпадают.");
        const result = await api.resetPassword(resetToken, password);
        setMode("login");
        setPassword("");
        setConfirmPassword("");
        setSuccess(result.message);
      } else if (mode === "register") {
        const result = await api.register(email, password, privacy);
        setToken(result.token);
        onAuthenticated(result.user);
      } else {
        const result = await api.login(email, password);
        if (result.requires_totp && result.challenge) {
          setChallenge(result.challenge);
          return;
        }
        if (!result.token || !result.user) throw new Error("Вход не завершён.");
        setToken(result.token);
        onAuthenticated(result.user);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось войти.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-layout">
      <section className="auth-story">
        <Logo />
        <div className="auth-copy">
          <span className="eyebrow">CAREERMOVE · НОВОЕ ПРИЛОЖЕНИЕ</span>
          <h1>Подходящие вакансии — без перезагрузок и лишнего шума</h1>
          <p>
            Один кабинет для поиска, проверки карточек и ручной отправки откликов.
            Устанавливается на телефон и компьютер.
          </p>
        </div>
        <div className="auth-points">
          <span><Icon name="shield" size={18} /> Данные пользователей изолированы</span>
          <span><Icon name="signal" size={18} /> Подборка обновляется на сервере</span>
          <span><Icon name="download" size={18} /> Работает как отдельное приложение</span>
        </div>
      </section>

      <section className="auth-panel" aria-label="Вход в CareerMove">
        <div className="auth-mobile-brand"><Logo /></div>
        {challenge ? (
          <>
            <span className="eyebrow">ДВУХФАКТОРНАЯ ЗАЩИТА</span>
            <h2>Подтвердите вход</h2>
            <p className="secondary">Введите шестизначный код из приложения-аутентификатора.</p>
          </>
        ) : mode === "forgot" ? (
          <>
            <button className="auth-back" onClick={() => changeMode("login")} type="button">
              <Icon name="arrowLeft" size={18} /> Вернуться ко входу
            </button>
            <span className="eyebrow">ВОССТАНОВЛЕНИЕ ДОСТУПА</span>
            <h2>Получить ссылку</h2>
            <p className="secondary">Отправим одноразовую ссылку на адрес вашего аккаунта.</p>
          </>
        ) : mode === "reset" ? (
          <>
            <span className="eyebrow">НОВЫЙ ПАРОЛЬ</span>
            <h2>Защитите аккаунт</h2>
            <p className="secondary">Придумайте новый пароль длиной не менее 8 символов.</p>
          </>
        ) : (
          <>
            <div className="auth-tabs" role="tablist" aria-label="Авторизация">
              <button
                aria-selected={mode === "login"}
                onClick={() => changeMode("login")}
                role="tab"
                type="button"
              >
                Войти
              </button>
              <button
                aria-selected={mode === "register"}
                onClick={() => changeMode("register")}
                role="tab"
                type="button"
              >
                Создать аккаунт
              </button>
            </div>
            <h2>{mode === "login" ? "С возвращением" : "Новый личный кабинет"}</h2>
            <p className="secondary">
              {mode === "login"
                ? "Ваши профили, вакансии и статусы останутся на месте."
                : "Новый пользователь не увидит чужие профили и историю."}
            </p>
          </>
        )}

        <form className="form-stack" onSubmit={submit}>
          {!challenge && (
            <>
              {mode !== "reset" && (
                <label>
                  <span>Email</span>
                  <input
                    autoComplete={mode === "register" || mode === "forgot" ? "email" : "username"}
                    inputMode="email"
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="name@example.com"
                    required
                    type="email"
                    value={email}
                  />
                </label>
              )}
              {mode !== "forgot" && (
                <div className="form-field">
                  <label htmlFor="auth-password">
                    {mode === "reset" ? "Новый пароль" : "Пароль"}
                  </label>
                  <div className="password-field">
                    <input
                      autoComplete={mode === "login" ? "current-password" : "new-password"}
                      id="auth-password"
                      minLength={8}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Минимум 8 символов"
                      required
                      type={showPassword ? "text" : "password"}
                      value={password}
                    />
                    <button
                      aria-label={showPassword ? "Скрыть пароль" : "Показать пароль"}
                      aria-pressed={showPassword}
                      className="password-toggle"
                      onClick={() => setShowPassword((visible) => !visible)}
                      title={showPassword ? "Скрыть пароль" : "Показать пароль"}
                      type="button"
                    >
                      <Icon name={showPassword ? "eyeOff" : "eye"} size={20} />
                    </button>
                  </div>
                </div>
              )}
              {mode === "reset" && (
                <label>
                  <span>Повторите пароль</span>
                  <input
                    autoComplete="new-password"
                    minLength={8}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder="Ещё раз новый пароль"
                    required
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                  />
                </label>
              )}
            </>
          )}
          {challenge && (
            <label>
              <span>Код подтверждения</span>
              <input
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setTotp(event.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                required
                value={totp}
              />
            </label>
          )}
          {mode === "register" && !challenge && (
            <label className="check-row">
              <input
                checked={privacy}
                onChange={(event) => setPrivacy(event.target.checked)}
                type="checkbox"
              />
              <span>Принимаю Privacy Policy и Terms beta-версии</span>
            </label>
          )}
          {error && <div className="inline-error" role="alert">{error}</div>}
          {success && <div className="inline-success" role="status">{success}</div>}
          <Button className="primary full" disabled={busy} type="submit">
            {busy
              ? "Подождите…"
              : challenge
                ? "Подтвердить"
                : mode === "login"
                  ? "Войти"
                  : mode === "register"
                    ? "Создать кабинет"
                    : mode === "forgot"
                      ? "Отправить ссылку"
                      : "Сохранить новый пароль"}
          </Button>
          {mode === "login" && !challenge && (
            <button className="auth-link" onClick={() => changeMode("forgot")} type="button">
              Не помню пароль
            </button>
          )}
          {mode === "reset" && (
            <button className="auth-link" onClick={() => changeMode("login")} type="button">
              Вернуться ко входу
            </button>
          )}
          {challenge && (
            <Button className="ghost full" onClick={() => setChallenge("")} type="button">
              Вернуться к входу
            </Button>
          )}
        </form>
        {!challenge && (
          <button className="auth-install" onClick={onInstall} type="button">
            <Icon name="download" size={18} />
            Установить на iPhone или компьютер
          </button>
        )}
      </section>
    </main>
  );
}

function MetricCard({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: number;
  note: string;
  tone: "blue" | "violet" | "teal" | "neutral";
}) {
  return (
    <article className={`metric-card ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function SearchStatus({ run }: { run: SearchRun | null }) {
  if (!run) return null;
  const active = run.status === "queued" || run.status === "running";
  const failed = run.status === "failed";
  return (
    <section className={`search-status ${active ? "active" : ""} ${failed ? "failed" : ""}`} aria-live="polite">
      <div className="status-indicator">
        {active ? <span className="spinner" /> : <Icon name={failed ? "close" : "check"} size={20} />}
      </div>
      <div className="status-copy">
        <strong>
          {active ? "Подборка обновляется в фоне" : failed ? "Поиск не завершён" : "Подборка готова"}
        </strong>
        <span>{run.detail || "Сервис готов к следующему запуску."}</span>
      </div>
      <span className="status-badge">
        {run.stage === "sources" ? "Источники" : run.stage === "matching" ? "Совпадения" : run.stage === "saving" ? "Сохранение" : active ? "В работе" : failed ? "Ошибка" : "Готово"}
      </span>
    </section>
  );
}

function jobFreshness(job: Job) {
  const raw = job.posted_at || job.verified_at || job.last_seen;
  if (!raw) return "Дата проверяется";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "Активность проверяется";
  const label = date.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  return job.posted_at ? `Опубликована ${label}` : `Активна в источнике · проверено ${label}`;
}

function JobCard({
  job,
  onStatus,
}: {
  job: Job;
  onStatus: (id: number, status: string) => Promise<void>;
}) {
  const [busy, setBusy] = useState("");
  const golden = job.score >= 80;
  const links = job.links?.length
    ? job.links
    : (job.link ? [{ url: job.link, source: job.source || "Источник" }] : []);

  async function act(next: string) {
    setBusy(next);
    try {
      await onStatus(job.id, next);
    } finally {
      setBusy("");
    }
  }

  return (
    <article className={`job-card ${golden ? "golden" : ""}`}>
      <div className="job-card-head">
        <div className={`score-ring ${golden ? "golden" : ""}`} aria-label={`Совпадение ${job.score}%`}>
          <strong>{job.score}</strong>
          <span>%</span>
        </div>
        <div className="job-title">
          <div className="chip-row">
            <span className={`chip ${golden ? "success" : "info"}`}>
              {golden ? "Золотая" : "Проверить"}
            </span>
            <span className="chip neutral">{job.candidate}</span>
          </div>
          <h3>{job.position || "Вакансия без названия"}</h3>
          <p>{job.company || "Компания не указана"} · {job.source || "Источник"}</p>
        </div>
      </div>

      <div className="job-facts">
        <span>{job.remote_location || "Удалённо — географию проверить"}</span>
        <span>{job.salary_text || "Зарплата не указана"}</span>
        <span>Компания {job.company_rating || 0}/100</span>
        <span>{jobFreshness(job)}</span>
      </div>

      {links.length > 0 && (
        <div className="source-links" aria-label="Ссылки на вакансию">
          <strong>{links.length > 1 ? `Найдена в ${links.length} источниках` : "Оригинал вакансии"}</strong>
          <div>
            {links.map((item, index) => (
              <a href={item.url} key={`${item.url}-${index}`} rel="noreferrer" target="_blank">
                <span>{item.source || `Источник ${index + 1}`}</span>
                <Icon name="external" size={16} />
              </a>
            ))}
          </div>
        </div>
      )}

      {(job.strengths || job.positioning) && (
        <div className="job-reason">
          <strong>Почему подходит</strong>
          <p>{job.strengths || job.positioning}</p>
        </div>
      )}
      {job.weaknesses && (
        <details>
          <summary>Что проверить перед откликом</summary>
          <p>{job.weaknesses}</p>
        </details>
      )}

      <div className="job-actions">
        <Button
          className="primary"
          disabled={Boolean(busy) || job.status === "approved"}
          icon="check"
          onClick={() => act("approved")}
        >
          {job.status === "approved" ? "Одобрено" : busy === "approved" ? "Сохраняем…" : "Одобрить"}
        </Button>
        <Button className="secondary" disabled={Boolean(busy)} icon="clock" onClick={() => act("later")}>
          Позже
        </Button>
        <Button className="ghost" disabled={Boolean(busy)} onClick={() => act("skip")}>
          Скрыть
        </Button>
      </div>
    </article>
  );
}

function CandidateCard({ candidate }: { candidate: Candidate }) {
  const ready = candidate.resume_count > 0;
  return (
    <article className="candidate-card">
      <div className="candidate-avatar">{candidate.name?.slice(0, 1).toUpperCase() || "?"}</div>
      <div className="candidate-main">
        <div className="candidate-title">
          <div>
            <h3>{candidate.name}</h3>
            <p>{candidate.target_title || "Целевая роль не указана"}</p>
          </div>
          <span className={`chip ${ready ? "success" : "warning"}`}>{ready ? "Готов" : "Нужно резюме"}</span>
        </div>
        <dl className="candidate-facts">
          <div><dt>Английский</dt><dd>{candidate.english_level || "—"}</dd></div>
          <div><dt>Минимум</dt><dd>${candidate.salary_min || 0}</dd></div>
          <div><dt>Резюме</dt><dd>{candidate.resume_count}</dd></div>
        </dl>
        <p className="candidate-geo">{candidate.desired_countries || "География не указана"}</p>
      </div>
    </article>
  );
}

function CandidateEditor({
  candidate,
  onSaved,
}: {
  candidate?: Candidate;
  onSaved: (saved: Candidate) => void;
}) {
  const [form, setForm] = useState<CandidateInput>(() => ({
    name: candidate?.name || "",
    target_title: candidate?.target_title || "QA Engineer",
    english_level: candidate?.english_level || "B1+",
    desired_countries: candidate?.desired_countries || "Serbia; Remote international",
    salary_min: candidate?.salary_min || 1300,
    notes: candidate?.notes || "Работа из Сербии; готова к интервью сейчас.",
    hard_exclude: candidate?.hard_exclude || "Sber; Sberbank; Сбер; Москва офис; Санкт-Петербург офис",
    hard_require: candidate?.hard_require || "remote; Serbia hybrid; official source",
    skills: candidate?.skills || [],
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    setError("");
    try {
      const saved = candidate
        ? await api.updateCandidate(candidate.id, form)
        : await api.createCandidate(form);
      onSaved(saved);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось сохранить профиль.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="settings-card profile-editor" onSubmit={submit}>
      <div className="section-heading compact">
        <div><h2>{candidate ? "Изменить профиль" : "Добавить профиль"}</h2><p>Профиль нужен для точного сравнения вакансий и выбора резюме.</p></div>
      </div>
      <div className="profile-form-grid">
        <label><span>Имя кандидата</span><input autoComplete="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Наталия" /></label>
        <label><span>Целевая роль</span><input autoComplete="organization-title" value={form.target_title} onChange={(e) => setForm({ ...form, target_title: e.target.value })} placeholder="Lead QA Engineer" /></label>
        <label><span>Английский</span><select value={form.english_level} onChange={(e) => setForm({ ...form, english_level: e.target.value })}><option>B1+</option><option>B2</option><option>C1</option><option>C2</option></select></label>
        <label><span>Минимальная зарплата, $</span><input inputMode="numeric" min={0} type="number" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: Number(e.target.value) || 0 })} /></label>
        <label className="wide"><span>География и формат</span><input value={form.desired_countries} onChange={(e) => setForm({ ...form, desired_countries: e.target.value })} placeholder="Serbia; Remote international" /></label>
        <label className="wide"><span>Что исключать <small>(необязательно)</small></span><input value={form.hard_exclude} onChange={(e) => setForm({ ...form, hard_exclude: e.target.value })} placeholder="Sber; офис Москва" /></label>
        <label className="wide"><span>Что обязательно <small>(необязательно)</small></span><input value={form.hard_require} onChange={(e) => setForm({ ...form, hard_require: e.target.value })} placeholder="remote; official source" /></label>
        <label className="wide"><span>Ключевые навыки <small>(через запятую)</small></span><input value={form.skills.join(", ")} onChange={(e) => setForm({ ...form, skills: e.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} placeholder="Поварское дело, меню, HACCP" /></label>
        <label className="wide"><span>Заметка</span><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></label>
      </div>
      {error && <div className="inline-error">{error}</div>}
      <div className="settings-actions"><Button className="primary" disabled={busy} type="submit">{busy ? "Сохраняем…" : "Сохранить профиль"}</Button></div>
    </form>
  );
}

function ResumeStudio({ candidate, onChanged }: { candidate: Candidate; onChanged: () => Promise<void> }) {
  const current = candidate.resumes?.[0];
  const [title, setTitle] = useState(current?.title || "Основное резюме");
  const [content, setContent] = useState(current?.content || "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function save() {
    if (content.trim().length < 20) { setMessage("Добавьте минимум 20 символов текста резюме."); return; }
    setBusy(true); setMessage("");
    try { await api.saveResume(candidate.id, { title, language: "EN", content }); await onChanged(); setMessage("Резюме сохранено приватно."); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Не удалось сохранить резюме."); }
    finally { setBusy(false); }
  }

  async function transform() {
    if (content.trim().length < 20) { setMessage("Сначала вставьте текст резюме."); return; }
    setBusy(true); setMessage("Очередь → загрузка → извлечение позиций → структурирование…");
    try { const result = await api.transformResume({ content, language: "EN" }); setContent(result.content); setMessage(result.message); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Преобразование не удалось."); }
    finally { setBusy(false); }
  }

  async function photo(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]; if (!file) return;
    setBusy(true); setMessage("Проверяем и прикрепляем фото приватно…");
    try { await api.uploadPhoto(candidate.id, file); await onChanged(); setMessage("Фото прикреплено к этому резюме."); }
    catch (caught) { setMessage(caught instanceof Error ? caught.message : "Не удалось прикрепить фото."); }
    finally { setBusy(false); event.target.value = ""; }
  }

  return <section className="settings-card profile-editor resume-studio">
    <div className="section-heading compact"><div><h2>Резюме и фото</h2><p>Данные доступны только вам и используются для подбора. Никуда не публикуются.</p></div></div>
    <div className="resume-row">
      <div className="resume-photo">{candidate.photo_data ? <img src={candidate.photo_data} alt="Фото кандидата" /> : <span>Фото</span>}</div>
      <label className="secondary upload-label">Прикрепить фото<input accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={photo} type="file" /></label>
    </div>
    <label><span>Название резюме</span><input value={title} onChange={(e) => setTitle(e.target.value)} /></label>
    <label><span>Текст резюме</span><textarea rows={8} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Вставьте текст резюме. PDF можно сохранить как текст здесь — форматирование останется под вашим контролем." /></label>
    {message && <div className={message.includes("не удалось") ? "inline-error" : "inline-success"}>{message}</div>}
    <div className="settings-actions"><Button className="secondary" disabled={busy} onClick={() => void transform()} type="button">{busy ? "Обрабатываем…" : "Улучшить структуру"}</Button><Button className="primary" disabled={busy} onClick={() => void save()} type="button">Сохранить резюме</Button></div>
  </section>;
}

function ApplicationsList({ applications }: { applications: Application[] }) {
  if (!applications.length) {
    return <EmptyState title="Откликов пока нет" text="После одобрения вакансии подготовьте письмо и подтвердите отправку вручную." />;
  }
  return (
    <div className="table-card">
      <div className="table-head">
        <span>Компания и позиция</span><span>Статус</span><span>Дата</span>
      </div>
      {applications.map((item) => (
        <div className="table-row" key={item.id}>
          <div>
            <strong>{item.company || "Компания"}</strong>
            <span>{item.position || "Вакансия"}</span>
          </div>
          <span className={`chip ${item.status === "sent" ? "success" : "info"}`}>{item.status}</span>
          <time>{item.created_at ? new Date(item.created_at).toLocaleDateString("ru-RU") : "—"}</time>
        </div>
      ))}
    </div>
  );
}

function AppearanceSettings({
  appearance,
  onSave,
}: {
  appearance: Appearance;
  onSave: (appearance: Appearance) => Promise<void>;
}) {
  const [draft, setDraft] = useState(appearance);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => setDraft(appearance), [appearance]);

  async function save() {
    setSaving(true);
    setSaved(false);
    try {
      await onSave(draft);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="settings-card">
      <div className="section-heading compact">
        <div>
          <span className="eyebrow">ИНТЕРФЕЙС</span>
          <h2>Внешний вид</h2>
          <p>Настройки применяются ко всему приложению и сохраняются в аккаунте.</p>
        </div>
      </div>
      <div className="settings-grid">
        <fieldset>
          <legend>Тема</legend>
          <div className="choice-grid themes">
            {([
              ["system-light", "Светлая", "Системная и спокойная"],
              ["system-dark", "Тёмная", "Контрастная для вечера"],
              ["cyber-aurora", "Cyber Aurora", "Фирменная, но читаемая"],
            ] as const).map(([value, label, note]) => (
              <label className={`choice-card ${draft.theme === value ? "selected" : ""}`} key={value}>
                <input
                  checked={draft.theme === value}
                  name="theme"
                  onChange={() => setDraft({ ...draft, theme: value })}
                  type="radio"
                />
                <span className={`theme-swatch ${value}`} />
                <strong>{label}</strong>
                <small>{note}</small>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend>Размер текста: {draft.font_scale}%</legend>
          <input
            aria-label="Размер текста"
            className="range"
            max="125"
            min="85"
            onChange={(event) => setDraft({ ...draft, font_scale: Number(event.target.value) })}
            step="5"
            type="range"
            value={draft.font_scale}
          />
          <div className="range-labels"><span>85%</span><span>100%</span><span>125%</span></div>
        </fieldset>

        <fieldset>
          <legend>Компоновка</legend>
          <div className="segmented">
            {([
              ["auto", "Авто"],
              ["compact", "Компактно"],
              ["comfortable", "Просторно"],
            ] as const).map(([value, label]) => (
              <label className={draft.density === value ? "selected" : ""} key={value}>
                <input
                  checked={draft.density === value}
                  name="density"
                  onChange={() => setDraft({ ...draft, density: value })}
                  type="radio"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <div className="settings-actions">
        <Button className="primary" disabled={saving} onClick={save}>
          {saving ? "Сохраняем…" : "Сохранить оформление"}
        </Button>
        {saved && <span className="saved-note"><Icon name="check" size={17} /> Сохранено</span>}
      </div>
    </section>
  );
}

function pushApplicationKey(value: string) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

function NotificationSettings() {
  const supported = (
    "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window
  );
  const [enabled, setEnabled] = useState(supported && Notification.permission === "granted");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(
    supported
      ? "После ежедневного поиска уведомление откроет новую подборку прямо в приложении."
      : "Установите CareerMove как приложение: на этом устройстве браузер не даёт доступ к push-уведомлениям.",
  );

  useEffect(() => {
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => setEnabled(Boolean(subscription)))
      .catch(() => undefined);
  }, [supported]);

  async function enable() {
    if (!supported || busy) return;
    setBusy(true);
    try {
      const config = await api.pushConfig();
      if (!config.enabled || !config.public_key) {
        throw new Error("Сервер уведомлений ещё не активирован.");
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Разрешение не выдано. Его можно включить в настройках браузера или системы.");
      }
      const registration = await navigator.serviceWorker.ready;
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: pushApplicationKey(config.public_key),
      });
      const data = subscription.toJSON();
      if (!data.endpoint || !data.keys?.p256dh || !data.keys.auth) {
        throw new Error("Браузер не вернул ключи push-подписки.");
      }
      await api.pushSubscribe({
        endpoint: data.endpoint,
        keys: { p256dh: data.keys.p256dh, auth: data.keys.auth },
      });
      setEnabled(true);
      setMessage("Уведомления включены. Они будут приходить на это устройство после автопоиска.");
    } catch (caught) {
      setMessage(caught instanceof Error ? caught.message : "Не удалось включить уведомления.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-card notification-settings">
      <div>
        <span className="eyebrow">АВТОПОИСК · 09:00 МСК</span>
        <h2>Уведомления на это устройство</h2>
        <p>{message}</p>
      </div>
      <Button
        className={enabled ? "secondary" : "primary"}
        disabled={!supported || busy}
        icon={enabled ? "check" : "signal"}
        onClick={() => void enable()}
      >
        {busy ? "Подключаем…" : enabled ? "Уведомления включены" : "Включить уведомления"}
      </Button>
    </section>
  );
}

function AppearanceToolbar({
  appearance,
  onChange,
}: {
  appearance: Appearance;
  onChange: (appearance: Appearance) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);

  async function apply(next: Appearance) {
    setBusy(true);
    try {
      await onChange(next);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="appearance-toolbar" aria-label="Быстрая настройка интерфейса">
      <div className="appearance-toolbar-copy">
        <Icon name="settings" size={18} />
        <div>
          <strong>Вид интерфейса</strong>
          <span>Настройте под свой экран</span>
        </div>
      </div>
      <label>
        <span>Тема</span>
        <select
          aria-label="Тема"
          disabled={busy}
          onChange={(event) => void apply({ ...appearance, theme: event.target.value as Appearance["theme"] })}
          value={appearance.theme}
        >
          <option value="system-light">Светлая</option>
          <option value="system-dark">Тёмная</option>
          <option value="cyber-aurora">Cyber Aurora</option>
        </select>
      </label>
      <label>
        <span>Текст</span>
        <select
          aria-label="Размер текста"
          disabled={busy}
          onChange={(event) => void apply({ ...appearance, font_scale: Number(event.target.value) })}
          value={appearance.font_scale}
        >
          <option value="90">90%</option>
          <option value="100">100%</option>
          <option value="110">110%</option>
          <option value="120">120%</option>
          <option value="125">125%</option>
        </select>
      </label>
      <label>
        <span>Отступы</span>
        <select
          aria-label="Плотность интерфейса"
          disabled={busy}
          onChange={(event) => void apply({ ...appearance, density: event.target.value as Appearance["density"] })}
          value={appearance.density}
        >
          <option value="auto">Авто</option>
          <option value="compact">Компактно</option>
          <option value="comfortable">Просторно</option>
        </select>
      </label>
    </section>
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen" aria-live="polite">
      <Logo />
      <span className="spinner large" />
      <p>Подключаем ваш кабинет…</p>
    </div>
  );
}

function InstallHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className="modal-layer" role="presentation" onMouseDown={onClose}>
      <section className="install-modal" role="dialog" aria-modal="true" aria-labelledby="install-title" onMouseDown={(event) => event.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Закрыть">
          <Icon name="close" />
        </button>
        <div className="modal-icon"><Icon name="download" size={28} /></div>
        <h2 id="install-title">Установить CareerMove</h2>
        <div className="install-steps">
          <div><strong>iPhone / iPad</strong><span>Откройте эту страницу в Safari → нажмите «Поделиться» → «На экран Домой» → «Добавить».</span></div>
          <div><strong>macOS</strong><span>Safari → Файл → «Добавить в Dock».</span></div>
          <div><strong>Windows / Android</strong><span>Откройте меню браузера и выберите «Установить приложение».</span></div>
        </div>
        <Button className="primary full" onClick={onClose}>Понятно</Button>
      </section>
    </div>
  );
}

export default function App() {
  const query = new URLSearchParams(window.location.search);
  const requestedView = query.get("view") as View | null;
  const [view, setView] = useState<View>(NAV_ITEMS.some((item) => item.id === requestedView) ? requestedView! : "today");
  const [user, setUser] = useState<User | null>(null);
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(Boolean(getToken()));
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<InstallPrompt | null>(null);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const [searching, setSearching] = useState(false);
  const [addingProfile, setAddingProfile] = useState(false);

  const appearance = user?.appearance || DEFAULT_APPEARANCE;
  const goldenJobs = useMemo(
    () => (dashboard?.jobs || []).filter((job) => job.score >= 80 && job.status !== "skip"),
    [dashboard],
  );
  const visibleJobs = useMemo(
    () => (dashboard?.jobs || []).filter((job) => job.status !== "skip"),
    [dashboard],
  );

  useEffect(() => {
    document.documentElement.dataset.theme = appearance.theme;
    document.documentElement.dataset.density = appearance.density;
    document.documentElement.style.setProperty("--font-scale", String(appearance.font_scale / 100));
    const themeColor = appearance.theme === "system-light" ? "#f7f9fc" : appearance.theme === "system-dark" ? "#0c111d" : "#07101a";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", themeColor);
  }, [appearance]);

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPrompt);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    Promise.all([api.me(), api.dashboard()])
      .then(([nextUser, nextDashboard]) => {
        setUser(nextUser);
        setDashboard(nextDashboard);
        if (query.get("action") === "search") setTimeout(() => void startSearch(), 0);
      })
      .catch((caught) => {
        setToken("");
        setError(caught instanceof Error ? caught.message : "Не удалось открыть кабинет.");
      })
      .finally(() => setLoading(false));
    // The initial session restore must run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);

  async function loadDashboard() {
    const next = await api.dashboard();
    setDashboard(next);
  }

  async function startSearch() {
    if (searching) return;
    setSearching(true);
    setError("");
    try {
      const result = await api.startSearch(true);
      const initial: SearchRun = {
        run_id: result.run_id,
        status: result.status as SearchRun["status"],
        stage: "queued",
        detail: result.detail || "Поиск поставлен в очередь.",
      };
      setDashboard((current) => current ? { ...current, search: initial } : current);
      // The worker continues on the server; the UI must not hold the user for
      // five minutes while sources and AI fallbacks are being processed.
      for (let attempt = 0; attempt < 45; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 1800));
        const run = await api.searchStatus(result.run_id);
        setDashboard((current) => current ? { ...current, search: run } : current);
        if (run.status === "completed" || run.status === "failed") {
          if (run.status === "completed") {
            await loadDashboard();
            setToast("Подборка обновлена");
          }
          break;
        }
      }
      const latest = await api.searchStatus(result.run_id).catch(() => null);
      if (latest && latest.status !== "completed" && latest.status !== "failed") {
        setDashboard((current) => current ? { ...current, search: latest } : current);
        setToast("Поиск продолжается на сервере — можно закрыть страницу");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось запустить поиск.");
    } finally {
      setSearching(false);
    }
  }

  async function setJobStatus(id: number, status: string) {
    const previous = dashboard;
    setDashboard((current) => current ? {
      ...current,
      jobs: current.jobs.map((job) => job.id === id ? { ...job, status } : job),
    } : current);
    try {
      await api.jobStatus(id, status);
      setToast(status === "approved" ? "Вакансия одобрена" : status === "later" ? "Сохранено на потом" : "Вакансия скрыта");
    } catch (caught) {
      setDashboard(previous);
      throw caught;
    }
  }

  async function saveAppearance(next: Appearance) {
    setUser((current) => current ? { ...current, appearance: next } : current);
    try {
      const saved = await api.appearance(next);
      setUser((current) => current ? { ...current, appearance: saved } : current);
    } catch (caught) {
      setUser((current) => current ? { ...current, appearance } : current);
      throw caught;
    }
  }

  async function install() {
    if (installPrompt) {
      await installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
    } else {
      setShowInstallHelp(true);
    }
  }

  async function logout() {
    try {
      await api.logout();
    } catch {
      // Local logout must still complete when the network is unavailable.
    }
    setToken("");
    setUser(null);
    setDashboard(null);
  }

  if (loading) return <LoadingScreen />;
  if (!user) {
    return (
      <>
        <AuthScreen
          onInstall={() => void install()}
          onAuthenticated={(nextUser) => {
            setUser(nextUser);
            setError("");
            setLoading(true);
            api.dashboard().then(setDashboard).catch((caught) => setError(caught.message)).finally(() => setLoading(false));
          }}
        />
        {showInstallHelp && <InstallHelp onClose={() => setShowInstallHelp(false)} />}
        {error && <div className="global-toast error" role="alert">{error}</div>}
      </>
    );
  }

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileMenu ? "open" : ""}`}>
        <div className="sidebar-head">
          <Logo />
          <button className="mobile-close" onClick={() => setMobileMenu(false)} aria-label="Закрыть меню">
            <Icon name="close" />
          </button>
        </div>
        <nav className="side-nav" aria-label="Основная навигация">
          {NAV_ITEMS.map((item) => (
            <button
              aria-current={view === item.id ? "page" : undefined}
              key={item.id}
              onClick={() => {
                setView(item.id);
                setMobileMenu(false);
              }}
            >
              <Icon name={item.icon} size={19} />
              <span>{item.label}</span>
              {item.id === "jobs" && dashboard?.metrics.golden ? <b>{dashboard.metrics.golden}</b> : null}
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="account-mini">
            <span>{user.email.slice(0, 1).toUpperCase()}</span>
            <div><strong>{user.email.split("@")[0]}</strong><small>{user.email}</small></div>
          </div>
          <Button className="ghost full" onClick={logout}>Выйти</Button>
        </div>
      </aside>

      {mobileMenu && <button className="scrim" onClick={() => setMobileMenu(false)} aria-label="Закрыть меню" />}

      <div className="workspace">
        <header className="topbar">
          <button className="menu-button" onClick={() => setMobileMenu(true)} aria-label="Открыть меню">
            <Icon name="menu" />
          </button>
          <div className="topbar-title">
            <strong>{NAV_ITEMS.find((item) => item.id === view)?.label}</strong>
            <span><i /> API подключён</span>
          </div>
          <div className="topbar-actions">
            <Button className="secondary install-button" icon="download" onClick={install}>Установить</Button>
            <Button className="primary" disabled={searching} icon="refresh" onClick={() => void startSearch()}>
              {searching ? "Ищем…" : "Обновить"}
            </Button>
          </div>
        </header>

        <main className="content">
          {error && (
            <div className="page-error" role="alert">
              <div><strong>Не удалось выполнить действие</strong><span>{error}</span></div>
              <button onClick={() => setError("")} aria-label="Закрыть"><Icon name="close" /></button>
            </div>
          )}

          {view === "today" && (
            <div className="page-stack">
              <AppearanceToolbar
                appearance={appearance}
                onChange={async (next) => {
                  try {
                    await saveAppearance(next);
                  } catch (caught) {
                    setError(caught instanceof Error ? caught.message : "Не удалось сохранить оформление.");
                  }
                }}
              />
              <section className="hero-panel">
                <div className="hero-copy">
                  <span className="eyebrow">CAREERMOVE · DAILY SIGNAL</span>
                  <h1>Только подходящие вакансии — без шума</h1>
                  <p>
                    Строгая география, реальный опыт каждого кандидата и ручное решение
                    перед отправкой отклика.
                  </p>
                  <div className="hero-actions">
                    <Button className="primary" disabled={searching} icon="refresh" onClick={() => void startSearch()}>
                      {searching ? "Подборка обновляется" : "Обновить подборку"}
                    </Button>
                    <Button className="secondary" icon="download" onClick={install}>Установить приложение</Button>
                  </div>
                </div>
                <div className="signal-card" aria-hidden="true">
                  {[44, 72, 56, 88, 64, 48, 76, 92, 58].map((height, index) => (
                    <i key={index} style={{ height: `${height}%` }} />
                  ))}
                </div>
              </section>

              <div className="network-note">
                <Icon name="shield" size={18} />
                <span><strong>Серверный поиск:</strong> {dashboard?.network || "подключение проверяется"}. VPN пользователю не нужен.</span>
              </div>

              <section className="metrics-grid" aria-label="Статистика">
                <MetricCard label="Найдено" value={dashboard?.metrics.found || 0} note="актуальных карточек" tone="blue" />
                <MetricCard label="Золотых" value={dashboard?.metrics.golden || 0} note="совпадение от 80%" tone="violet" />
                <MetricCard label="Отправлено" value={dashboard?.metrics.sent || 0} note="подтверждено вручную" tone="teal" />
                <MetricCard label="Источников" value={dashboard?.metrics.sources || 0} note="включено в поиск" tone="neutral" />
              </section>

              <SearchStatus run={dashboard?.search || null} />

              <section>
                <div className="section-heading">
                  <div>
                    <span className="eyebrow">ГЛАВНЫЕ СОВПАДЕНИЯ</span>
                    <h2>Золотые вакансии</h2>
                    <p>Сначала проверяйте эти карточки. Ничего не отправляется автоматически.</p>
                  </div>
                  <button className="text-button" onClick={() => setView("jobs")}>Все вакансии <span>→</span></button>
                </div>
                {goldenJobs.length ? (
                  <div className="jobs-grid">
                    {goldenJobs.slice(0, 4).map((job) => <JobCard job={job} key={job.id} onStatus={setJobStatus} />)}
                  </div>
                ) : (
                  <EmptyState
                    title="Сегодня золотых вакансий пока нет"
                    text="Запустите обновление. Строгий фильтр лучше пустого списка нерелевантных ролей."
                    action={<Button className="primary" icon="refresh" onClick={() => void startSearch()}>Запустить поиск</Button>}
                  />
                )}
              </section>
            </div>
          )}

          {view === "jobs" && (
            <div className="page-stack">
              <div className="section-heading page-title">
                <div>
                  <span className="eyebrow">КАРТОЧКИ</span>
                  <h1>Подходящие вакансии</h1>
                  <p>Действия меняют только выбранную карточку — страница не перезагружается.</p>
                </div>
                <Button className="primary" disabled={searching} icon="refresh" onClick={() => void startSearch()}>
                  {searching ? "Обновляем…" : "Новый поиск"}
                </Button>
              </div>
              <SearchStatus run={dashboard?.search || null} />
              {visibleJobs.length ? (
                <div className="jobs-grid">
                  {visibleJobs.map((job) => <JobCard job={job} key={job.id} onStatus={setJobStatus} />)}
                </div>
              ) : (
                <EmptyState title="Карточек пока нет" text="Добавьте профиль и запустите поиск подходящих вакансий." />
              )}
            </div>
          )}

          {view === "profiles" && (
            <div className="page-stack">
              <div className="section-heading page-title">
                <div>
                  <span className="eyebrow">КАНДИДАТЫ И РЕЗЮМЕ</span>
                  <h1>Профили</h1>
                  <p>Каждая вакансия оценивается отдельно для каждого опыта.</p>
                </div>
                {dashboard?.candidates.length ? <Button className="secondary" onClick={() => setAddingProfile((value) => !value)}>{addingProfile ? "Отменить" : "Добавить ещё"}</Button> : null}
              </div>
              {(!dashboard?.candidates.length || addingProfile) && <CandidateEditor
                candidate={addingProfile ? undefined : dashboard?.candidates[0]}
                onSaved={(saved) => {
                  setDashboard((current) => current ? {
                    ...current,
                    candidates: current.candidates.some((item) => item.id === saved.id)
                      ? current.candidates.map((item) => item.id === saved.id ? saved : item)
                      : [...current.candidates, saved],
                    metrics: { ...current.metrics, candidates: current.candidates.some((item) => item.id === saved.id) ? current.metrics.candidates : current.metrics.candidates + 1 },
                  } : current);
                  setToast("Профиль сохранён");
                  setAddingProfile(false);
                }}
              />}
              {dashboard?.candidates.length ? (
                <>
                  <div className="candidate-grid">{dashboard.candidates.map((candidate) => <CandidateCard candidate={candidate} key={candidate.id} />)}</div>
                  <ResumeStudio candidate={dashboard.candidates[0]} onChanged={loadDashboard} />
                </>
              ) : (
                <EmptyState title="Создайте первый профиль" text="Заполните форму выше: вакансия будет оцениваться по этому опыту отдельно." />
              )}
            </div>
          )}

          {view === "applications" && (
            <div className="page-stack">
              <div className="section-heading page-title">
                <div>
                  <span className="eyebrow">РУЧНАЯ ОТПРАВКА</span>
                  <h1>Отклики</h1>
                  <p>Готово, отправлено, ошибка или дубликат — статусы остаются прозрачными.</p>
                </div>
              </div>
              <ApplicationsList applications={dashboard?.applications || []} />
            </div>
          )}

          {view === "settings" && (
            <div className="page-stack">
              <div className="section-heading page-title">
                <div>
                  <span className="eyebrow">ПЕРСОНАЛИЗАЦИЯ</span>
                  <h1>Настройки</h1>
                  <p>Только необходимые параметры. Остальные инструменты не мешают основному сценарию.</p>
                </div>
              </div>
              <AppearanceSettings appearance={appearance} onSave={saveAppearance} />
              <NotificationSettings />
              <section className="settings-card install-settings">
                <div>
                  <span className="eyebrow">ПРИЛОЖЕНИЕ</span>
                  <h2>Установить CareerMove</h2>
                  <p>После установки CareerMove открывается отдельным окном и доступен из Dock или с домашнего экрана.</p>
                </div>
                <Button className="primary" icon="download" onClick={install}>Показать установку</Button>
              </section>
            </div>
          )}
        </main>
      </div>

      {showInstallHelp && <InstallHelp onClose={() => setShowInstallHelp(false)} />}

      {toast && <div className="global-toast" role="status"><Icon name="check" size={18} /> {toast}</div>}
    </div>
  );
}
